import { useCallback, useEffect, useMemo, useState } from "react"
import { useAppDispatch, useAppSelector } from "../../../store/app/hooks"
import {
  setOpenAlert,
  setLoadingDelete,
  setCloseAlert,
} from "../../../store/features/alert/alertSlice"
import { useLazyQuery, useMutation, useQuery } from "@apollo/client"
import {
  selectCategoryPagination,
  selectCategoryQuery,
  setCategoryPagination,
  setCategoryQuery,
} from "../../../store/features/category/categorySlice"
import { graphql } from "../../../gql"
import CustomTable from "../../Common/CustomTable/CustomTable"
import Loader from "../../Common/Loader/Loader"
import { getErrorsAsString } from "../../../utils/getErrorsAsString"
import {
  Category,
  CategoryWhereInput,
  QueryMode,
  SortOrder,
} from "../../../gql/graphql"
import { MRT_ColumnDef, MRT_ColumnFiltersState } from "material-react-table"
import { Pagination } from "../../../types/Pagination"
import { setOpenSnackbar } from "../../../store/features/snackbar/snackbarSlice"
import { updateCacheCategory } from "../../../caches/updateCacheCategory"
import { useNavigate } from "react-router-dom"
import { setTitle } from "../../../store/features/menu/menuSlice"
import { havePermissions } from "../../../utils/permissions"
import { getUserCountryId } from "../../../utils/authToken"
import { whereInputClause } from "../../../utils/permissions"
import useLang from "../../../hooks/useLang"
import ToogleVisibleCategory from "../../Common/ToogleVisibleCategory/ToogleVisibleCategory"

export const LIST_CATEGORIES = graphql(`
  query Categories(
    $where: CategoryWhereInput
    $orderBy: [CategoryOrderByWithRelationAndSearchRelevanceInput!]
    $take: Int
    $skip: Int
  ) {
    categories(where: $where, orderBy: $orderBy, take: $take, skip: $skip) {
      id
      name_fr
      name_en
      isVisible
      Segmentation {
        name_fr
        id
        name_en
      }
    }
    aggregateCategory {
      _count {
        _all
      }
    }
  }
`)

export const DELETE_CATEGORY = graphql(`
  mutation DeleteOneCategory($where: CategoryWhereUniqueInput!) {
    deleteOneCategory(where: $where) {
      id
      name_fr
      name_en
      Segmentation {
        id
        name_fr
        name_en
      }
    }
  }
`)

const LIST_FILTER = graphql(`
  query filtersListCategory {
    segmentations {
      id
      name_fr
    }
  }
`)

const ListCategory = () => {
  const { page, size } = useAppSelector(selectCategoryPagination)
  const query = useAppSelector(selectCategoryQuery)
  const dispatch = useAppDispatch()
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([])
  const { loading: loadingFilter, data: dataFilter } = useQuery(LIST_FILTER)
  const { getText } = useLang()
  const countryId = getUserCountryId()

  const navigate = useNavigate()

  const getValueFilter = (el: { name_fr: string; id: number }) => {
    return {
      label: el.name_fr,
      value: el.id.toString(),
      text: el.name_fr,
    }
  }

  const getFilters: () => CategoryWhereInput = useCallback(() => {
    if (columnFilters.length) {
      return columnFilters.reduce(
        (
          accumulator: CategoryWhereInput,
          { id, value }: { id: string; value: any },
        ) => {
          switch (id) {
            case "Segmentation":
              accumulator = {
                ...accumulator,
                Segmentation: {
                  is: {
                    id: {
                      equals: Number(value),
                    },
                  },
                },
              }
              break
            case "name_fr":
              accumulator = {
                ...accumulator,
                OR: [
                  {
                    name_fr: {
                      search: value,
                    },
                  },
                  {
                    name_fr: {
                      contains: value,
                    },
                  },
                ],
              }
              break
            case "id":
              accumulator = {
                ...accumulator,
                id: {
                  equals: Number(value),
                },
              }
              break
            case "name_en":
              accumulator = {
                ...accumulator,
                OR: [
                  {
                    name_en: {
                      search: value,
                    },
                  },
                  {
                    name_en: {
                      contains: value,
                    },
                  },
                ],
              }
              break
          }
          return accumulator
        },
        {},
      )
    }
    return {}
  }, [columnFilters])

  const queryName = useCallback(() => {
    return query
      ? {
          OR: [
            { name_fr: { contains: query, mode: QueryMode.Insensitive } },
            { name_en: { contains: query, mode: QueryMode.Insensitive } },
          ],
        }
      : {}
  }, [query])

  const [getCategories, { loading, data }] = useLazyQuery(LIST_CATEGORIES)

  useEffect(() => {
    getCategories({
      variables: {
        take: size,
        skip: page * size,
        orderBy: { name_fr: SortOrder.Asc },
        where: {
          ...whereInputClause({
            Countries: {
              some: {
                id: {
                  equals: countryId,
                },
              },
            },
          }),
          ...queryName(),
          ...getFilters(),
        },
      },
    })
  }, [getCategories, page, query, queryName, size, getFilters])

  const [deleteCategory] = useMutation(DELETE_CATEGORY)

  const columns = useMemo<MRT_ColumnDef<Category>[]>(
    () => [
      {
        accessorKey: "id",
        header: "ID",
        enableEditing: false,
        size: 80,
        enableClickToCopy: true,
        filterVariant: "text",
        id: "id",
      },
      {
        accessorFn: (row) => (
          <ToogleVisibleCategory id={row.id} isVisible={row.isVisible} />
        ),
        header: "Statut de visibilité",
        enableEditing: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "name_fr",
        header: getText("Nom en fr"),
        enableClickToCopy: true,
        filterVariant: "text",
        id: "name_fr",
      },
      {
        accessorKey: "name_en",
        header: getText("Nom en en"),
        enableClickToCopy: true,
        filterVariant: "text",
        id: "name_en",
      },
      {
        accessorKey: "Segmentation.name_fr",
        header: getText("Segmentation"),
        enableClickToCopy: true,
        filterVariant: "select",
        filterSelectOptions: dataFilter?.segmentations.map((el) =>
          getValueFilter(el),
        ),
        id: "Segmentation",
      },
    ],
    [dataFilter, getText],
  )

  const handleChangePagination = (pagination: Pagination) =>
    dispatch(setCategoryPagination(pagination))

  const handleValidDelete = (id: number) => {
    dispatch(setLoadingDelete(true))
    deleteCategory({
      variables: {
        where: {
          id,
        },
      },
      onCompleted: () => {
        dispatch(
          setOpenSnackbar({
            message: getText("La categorie a été supprimée avec succès"),
            status: "success",
          }),
        )
        dispatch(setLoadingDelete(false))
        dispatch(setCloseAlert())
      },
      onError: (err) => {
        const message = getErrorsAsString(err)
        dispatch(setCloseAlert())
        dispatch(setOpenSnackbar({ message }))
      },
      update: (cache, { data }) => {
        updateCacheCategory({
          action: "delete",
          cache,
          entryData: data?.deleteOneCategory,
        })
      },
    })
  }

  const handleOpenDeleteDialog = (id: number) => {
    dispatch(
      setOpenAlert({
        handleValid: () => handleValidDelete(id),
        message: getText(
          "Êtes-vous vraiment sûr de vouloir supprimer cette category ?",
        ),
        isLoading: false,
      }),
    )
  }

  const onChangeSearchValue = (value: string) =>
    dispatch(setCategoryQuery(value))

  const handleEdit = (id: number) => {
    dispatch(setTitle(getText("Modifier une catégorie")))
    navigate(`/category/edit/${id}`)
  }

  const handleView = (id: number) => {
    dispatch(setTitle(getText("Detail d'une catégorie")))
    navigate(`/category/detail/${id}`)
  }

  const handleRefresh = () => {
    window.location.reload()
  }

  if (loadingFilter) return <Loader />

  return (
    <CustomTable
      enableColumnFilters
      columns={columns}
      data={data?.categories || []}
      lableAddNew={getText("Créer une nouvelle catégorie")}
      rootLisName={"/category/add"}
      isLoading={loading}
      showProgressBars={loading}
      rowCount={data?.aggregateCategory._count?._all || 0}
      handleChangePagination={handleChangePagination}
      pageIndex={page}
      pageSize={size}
      searchValue={query}
      onChangeSearchValue={onChangeSearchValue}
      handleDelete={
        havePermissions(["category.delete"])
          ? handleOpenDeleteDialog
          : undefined
      }
      isHaveHeaderAction={havePermissions(["category.create"])}
      handleEdit={havePermissions(["category.change"]) ? handleEdit : undefined}
      isHaveView={havePermissions(["category.view"])}
      handleView={havePermissions(["category.view"]) ? handleView : undefined}
      isHaveAction={havePermissions([
        "category.view",
        "category.change",
        "category.delete",
      ])}
      handleRefresh={handleRefresh}
      columnFilters={columnFilters}
      setColumnFilters={setColumnFilters}
    />
  )
}

export default ListCategory
